import React from 'react'

import { formatNumberWithCommas, isValidUrl } from '../utils/shared.js';
import ProductForm from './ProductForm.jsx';
import ConsumablesForm from './forms/ConsumablesForm.jsx';
import RentalForm from './forms/RentalForm.jsx';
import OreSaleForm from './forms/OreSaleForm.jsx';
import MineSaleForm from './forms/MineSaleForm.jsx';
import RefurbishingForm from './forms/RefurbishingForm.jsx';
import RebuildBasePriceForm from './RebuildBasePriceForm.jsx';
import MediaManager from './forms/MediaManager.jsx';

export default function EditListingModal({ isOpen, onClose, listing, onListingUpdated, categories }) {
    const [formData, setFormData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [formSuccess, setFormSuccess] = React.useState(null);
    const [openSection, setOpenSection] = React.useState('details');
    const [uploadingImages, setUploadingImages] = React.useState({}); // NEW: State for image loading spinners

    React.useEffect(() => {
        if (isOpen && listing) {
            console.log("Fetching details for listing:", listing);
            const fetchDetails = async () => {
                setLoading(true);
                setError(null);
                setFormSuccess(null);
                setFormData(null);
                try {
                    const response = await fetch(`/api/get_listing_details.php?id=${listing.id}&type=${listing.listingType}`, { credentials: 'include' }); // Updated this for rebuild listing type.
                    if (!response.ok) {
                        const errData = await response.json();
                        throw new Error(errData.error || 'Failed to fetch listing details.');
                    }
                    const data = await response.json();

                    // Ensure form_type is set for ProductForm logic (specifically for rebuilds)
                    if (!data.form_type && data.listingType) {
                        data.form_type = data.listingType;
                    }
                    
                     // --- Normalize image and doc URLs (important for edits/drafts) ---
                     for (let i = 1; i <= 10; i++) {
                        const imageKey = `image_url_${i}`;
                        if (data && data[imageKey]) {
                            // Images get put directly into formData, keyed by URL
                            // We do NOT want to load file objects here
                            if (!isValidUrl(data[imageKey])) console.warn(`[EditListingModal] Invalid image URL detected during draft load: ${data[imageKey]}`);
                            console.log(`[EditListingModal] Loaded draft image ${i}:`, data[imageKey]);
                        }
                     }

                     const docFields = ['doc_manual_url', 'doc_datasheet_url', 'doc_conformity_url', 'doc_calibration_url', 'doc_warranty_url', 'doc_service_history_url', 'doc_factory_test_url', 'doc_oil_report_url'];
                     docFields.forEach(field => {
                         if (data && data[field]) {
                             console.log(`[EditListingModal] Loaded draft doc:`, field, data[field]);
                             if (!isValidUrl(data[field])) console.warn(`[EditListingModal] Invalid doc URL detected during draft load: ${data[field]}`);
                         }
                     });
                     //--- NEW: Handle dynamic datapack uploads (1-25) ---
                     for (let i = 1; i <= 25; i++) {
                        const docKey = `doc_datasheet_${i}_url`;
                        // --- FIX: Only validate and log if the key exists and has a value ---
                        if (data && data[docKey]) {
                            if (!isValidUrl(data[docKey])) {
                                console.warn(`[EditListingModal] Invalid datasheet URL detected for ${docKey}:`, data[docKey]);
                            }
                            console.log(`[EditListingModal] Loaded draft datapack ${i}:`, data[docKey]);
                        }
                     }

                    // --- Price Deflation Logic ---
                    // The user should see and edit their desired payout, not the inflated list price.
                    // This should only apply to purchasable items, not rentals or services.
                    const isPurchasable = data && !['rental', 'service'].includes(data.listingType);
                    if (isPurchasable) {
                        const priceField = ['price', 'rate_per_hour', 'base_price'].find(field => data && data[field] !== undefined && data[field] !== null);
    
                        if (priceField) {
                            const inflatedPrice = parseFloat(data[priceField]);
                            if (inflatedPrice > 0) {
                                // Deflate the price to get the seller's payout amount.
                                // Use the globally available fee percentage.
                                const payoutPrice = inflatedPrice * (1 - (window.APP_CONFIG?.platformFeePercentage || 0.06));
                                // Set the form to show the rounded payout price, suitable for an input field.
                                data[priceField] = payoutPrice.toFixed(2);
                            }
                        }
                    }
                    // --- End of Price Deflation Logic ---

                    const maxImages = 10;
                    // Add file placeholders for the image previews
                    for (let i = 1; i <= maxImages; i++) {
                        data[`image_file_${i}`] = null;
                    }
                    
                    // --- NEW: Parse rebuild_details if it exists ---
                    if (data.rebuild_details && typeof data.rebuild_details === 'string') {
                        try {
                            data.rebuild_details = JSON.parse(data.rebuild_details);
                        } catch (e) {
                            console.error("Failed to parse rebuild_details JSON:", e);
                            data.rebuild_details = {};
                        }
                    } else if (!data.rebuild_details) {
                        data.rebuild_details = {};
                    }

                    setFormData(data);
                } catch (err) {
                    console.error("Error fetching listing details:", err);
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
            fetchDetails();
        }
    }, [isOpen, listing]);

    // --- NEW: Centralized file upload handler ---
    const handleFileUpload = async (files, fieldName) => {
        if (files.length === 0) return;

        console.log(`[EditListingModal] handleFileUpload called for field: ${fieldName} with files:`, files);

        const isImageUpload = fieldName === 'image_files';
        setIsSubmitting(true); // Use this as a generic "processing" indicator
        setError(null);

        // --- NEW: Client-side file size validation (100MB max) ---
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        const oversizedFiles = files.filter(f => f.size > maxSize);
        if (oversizedFiles.length > 0) {
            const errorMessage = `File(s) exceed 100MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`;
            console.error(`[EditListingModal] ${errorMessage}`);
            setError(errorMessage);
            setIsSubmitting(false);
            return;
        }

        const uploadFormData = new FormData();
        let hasFilesToUpload = false;

        if (fieldName === 'image_files' || fieldName === 'doc_datasheet_files') {
            // Handle multi-file uploads
            const prefix = fieldName === 'image_files' ? 'image' : 'doc_datasheet';
            const maxSlots = prefix === 'image' ? 10 : 25; 
            
            const newUploadingState = {};
            let currentFileIndex = 1;
            for (const file of files) {
                while (currentFileIndex <= maxSlots && formData[`${prefix}_url_${currentFileIndex}`]) {
                    currentFileIndex++;
                }
                if (currentFileIndex > maxSlots) {
                    alert(`Maximum number of ${prefix}s reached.`);
                    break;
                }
                uploadFormData.append(`${prefix}_${currentFileIndex}`, file);
                hasFilesToUpload = true;
                if (isImageUpload) {
                    // Mark this slot as uploading
                    newUploadingState[currentFileIndex] = true;
                }
                currentFileIndex++;
            }
        } else {
            // Handle single file uploads
            uploadFormData.append(fieldName.replace('_file', ''), files[0]); 
            hasFilesToUpload = true;
        }

        if (!hasFilesToUpload) {
            setIsSubmitting(false);
            if (isImageUpload) {
                // Clear any uploading state if no files were actually added
                setUploadingImages({});
            }
            return;
        }

        try {
            const response = await fetch('/api/upload_files.php', { method: 'POST', credentials: 'include', body: uploadFormData });
            const result = await response.json();
            if (!result.success) throw new Error(result.message || 'File upload failed.');

            // Update form state with the new URLs
            setFormData(prev => ({ ...prev, ...result.urls }));
            setFormSuccess('File(s) uploaded successfully.');
        } catch (err) {
            setError(`Upload failed: ${err.message}`);
        } finally {
            // --- NEW: Clear uploading state after completion ---
            if (isImageUpload) {
                setUploadingImages({});
            }
            setIsSubmitting(false);
        }
    };

    const handleFormChange = (e) => {
        console.log("[EditListingModal] handleFormChange called for event:", e);
        const { name, value, type, files, checked } = e.target;

        if (type === 'checkbox' && name.startsWith('is_')) {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (type === 'file' && files && files.length > 0) {
            console.log("[EditListingModal] File selected:", name, files);
            handleFileUpload(Array.from(files), name);
            e.target.value = ''; // Reset file input
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRemoveDataPackFile = (index) => {
        setFormData(prev => ({
            ...prev,
            [`doc_datasheet_${index}_url`]: null
        }));
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            [`image_url_${index}`]: null
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setFormSuccess(null);

        try {
            // With instant uploads, file handling is already done.
            const payload = { ...formData };

            // --- NEW: De-format price before submission ---
            const formattedFields = ['price', 'base_price', 'rate_per_hour', 'quantity', 'hours', 'year', 'quantity_available'];
            formattedFields.forEach(field => {
                if (payload[field]) {
                    payload[field] = payload[field].toString().replace(/,/g, '');
                }
            });

            if (payload.rebuild_details) {
                const cleanDetails = typeof payload.rebuild_details === 'string' ? JSON.parse(payload.rebuild_details) : { ...payload.rebuild_details };
                Object.keys(cleanDetails).forEach(key => {
                    if (cleanDetails[key]?.hours) {
                        cleanDetails[key].hours = cleanDetails[key].hours.toString().replace(/,/g, '');
                    }
                });
                payload.rebuild_details = JSON.stringify(cleanDetails);
            }
            // --- End of new logic ---

            Object.keys(payload).forEach(key => {
                if (key.includes('_file')) delete payload[key];
            });

            const updateResponse = await fetch('/api/update_listing.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const updateResult = await updateResponse.json();
            if (!updateResponse.ok) throw new Error(updateResult.message || 'Failed to update listing.');

            setFormSuccess(updateResult.message);
            if (onListingUpdated) onListingUpdated();
            setTimeout(onClose, 2500);

        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleSection = (id) => {
        setOpenSection(prev => prev === id ? null : id);
    };

    const renderForm = () => {
        if (!formData) return null;

        console.log("[EditListingModal] Rendering form with data:", formData);

        const props = {
            formData,
            onFormChange: handleFormChange,
            onRemoveImage: handleRemoveImage,
            onRemoveDataPackFile: handleRemoveDataPackFile, // NEW
            openSection,
            onToggleSection: handleToggleSection,
            categories,
            isEdit: true,
            uploadingImages, // Pass down the new state
        };

        return (
            <form onSubmit={handleSubmit} className="space-y-6">
                {(() => {
                    switch (formData.listingType) {
                        case 'standard':
                        case 'wots':
                        case 'diesel': return <ProductForm {...props} />;
                        case 'rental': return <RentalForm {...props} />;
                        case 'consumable': return <ConsumablesForm {...props} />;
                        case 'ore': return <OreSaleForm {...props} />;
                        case 'mine': return <MineSaleForm {...props} />;
                        case 'service': return <RefurbishingForm {...props} />;
                        case 'rebuild': return (
                            <div className="space-y-2">
                                <ProductForm {...props} />
                                <RebuildMatrixForm {...props} />
                                <RebuildBasePriceForm {...props} />
                            </div>
                        );
                        default: return <p className="text-red-500">Error: Unknown listing type "{formData.listingType}"</p>;
                    }
                })()}
                {/* Display error/success messages here for all form types */}
                {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mt-4">{error}</div>}
                {formSuccess && <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mt-4">{formSuccess}</div>}

                <div className="flex justify-end space-x-4 pt-6 mt-6 border-t">
                    <button type="button" onClick={onClose} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="primary-gradient text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50">
                        {isSubmitting ? 'Saving...' : 'Save & Resubmit for Approval'}
                    </button>
                </div>
            </form>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-bold text-gray-900">Edit Listing</h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <i className="fas fa-times text-xl"></i>
                        </button>
                    </div>
                    
                    {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-500"></i><p className="mt-2">Loading listing details...</p></div>}

                    {formData && renderForm()}
                </div>
            </div>
        </div>
    );
}
